import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isDefined } from '@ngx-translate/core/src/util';
import { PioneerUtils } from 'app/event/utils/pioneer-utils';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { User, UserType } from '../../auth/models/User';
import { LogService } from '../../shared/log/log.service';
import { MultiAgency } from '../../shared/models/multi-agency';
import { UserToken } from '../models/user-token.model';
import { CustomerConfig } from './../../auth/models/Customer-config';
import { AuthenticationHttpService } from './authentication.http.service';
import { UserLocalStorageService } from './user.local.storage.service';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { UserJti } from '../models/user-jti.model';

@Injectable()
export class AuthenticationStore {

  public static readonly TOKEN_NAME = 'id_token';
  private static readonly EXTERNAL_LOGIN = 'external_login';
  private static readonly DEFAULT_AGENCY_STR: string = '_DEFAULT_AGENCY';
  public static readonly CURRENT_AGENCY_STR: string = '_CURRENT_AGENCY';
  public static readonly MULTI_AGENCY_STR = 'MULTI_AGENCY';
  public static readonly EXP_TOKEN_NAME = 'exp_token';

  private userAuthenticated: ReplaySubject<boolean> = new ReplaySubject(1);
  private userBehaviorSubject: BehaviorSubject<User> = new BehaviorSubject(new User());

  private defaultAgency: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  defaultAgency$: Observable<string> = this.defaultAgency.asObservable();

  private isClientContactSetUp: boolean;
  private clientContactEditable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  clientContactEditable$: Observable<boolean> = this.clientContactEditable.asObservable();

  constructor(
    private authenticationHttpService: AuthenticationHttpService,
    private logService: LogService,
    private userLocalStorageService: UserLocalStorageService,
    private router: Router,
    private environment: EnvironmentConfiguration
  ) {
    this.init();
  }

  protected init(): void {
    this.isClientContactSetUp = false;
    this.clientContactEditable.next(true);
    const encodedToken = this.getToken();
    this.setUserFromToken(encodedToken, null);
    this.setClientContactEditable(encodedToken);
  }

  get user(): Observable<User> {
    return this.userBehaviorSubject.asObservable();
  }

  login(username: string, password: string): Observable<boolean> {
    const login: Observable<UserToken> = this.authenticationHttpService.login(username, password);

    return login.map((userPermission: UserToken) => {
        if (userPermission && userPermission.token) {
          // set token property
          const tokenString = userPermission.token;

          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(false);
          this.updateState();
          this.setUserFromToken(userPermission.token, null);
          this.userLocalStorageService.resetPagesizes();
          // return true to indicate successful login
          return true;
        } else {
          this.userLocalStorageService.removeUserToken();

          // return false to indicate failed login
          return false;
        }
      }
    );
  }

  refreshAuthentication(): Observable<UserToken> {
    return this.refreshPermissionsFromToken(this.getToken(), this.isExternalLogin());
  }

  handleJtiFromExternalSource(token: string): Observable<UserJti> {
    return this.getJtiFromToken(token);
  }

  handleTokenFromExternalSource(token: string, jti: string): Observable<UserToken> {
    return this.generatePermissionsFromToken(token, jti, true);
  }

  getJtiFromToken(token: string): Observable<UserJti> {

    return this.authenticationHttpService.getJti(token)
      .catch(error => {
        this.logService.error(`Failed to get jti`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(jti => {
        // Make sure we got a valid response back from the server
        if (jti && jti.jti) {
          this.userLocalStorageService.setExpToken(jti.exp);
          return jti;
        } else {
          this.logService.error(`Received invalid Jti`);
          throw new Error('Invalid Jti');
        }
      });
  }

  generatePermissionsFromToken(token: string, jti: string, externalLogin: boolean): Observable<UserToken> {
    // Make sure token is not expired
    if (this.isTokenExpired()) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    let agencyId: string = null;
    if (jti) {
      let defaultArcIataJson: string = this.userLocalStorageService.getItem(jti
                                                                            + AuthenticationStore.DEFAULT_AGENCY_STR);
      if (defaultArcIataJson != null && defaultArcIataJson.length > 0) {
        let defaultArcIata: MultiAgency = JSON.parse(defaultArcIataJson);
        if (defaultArcIata && defaultArcIata.agencyId
                          && defaultArcIata.agencyId != null
                          && defaultArcIata.agencyId.length > 0) {
          agencyId = defaultArcIata.agencyId;
        }
      }
    }
    return this.authenticationHttpService.getPermissions(token, agencyId)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token, null);

          if (userPermission && userPermission.agencyInfo
                              && userPermission.agencyInfo.defaultArcIata
                              && userPermission.agencyInfo.defaultArcIata != null) {
            this.userLocalStorageService.setItem(userPermission.jti.toUpperCase() + AuthenticationStore.DEFAULT_AGENCY_STR,
                                                 JSON.stringify(userPermission.agencyInfo.defaultArcIata));
            this.userLocalStorageService.setItem(userPermission.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR,
                                                 JSON.stringify(userPermission.agencyInfo.defaultArcIata));
            this.userLocalStorageService.setExpToken(userPermission.exp);
            this.defaultAgency.next(userPermission.agencyInfo.defaultArcIata);
          }

          if (userPermission && userPermission.agencyInfo) {
            this.authenticationHttpService.retrieveAgencies(userPermission.token).subscribe(
              (data: MultiAgency[]) => {
                if (data && data.length > 1) {
                  let agencies = List<MultiAgency>();
                  data.forEach(agency => {
                    let item = new MultiAgency(
                      {
                        'agencyId': PioneerUtils.safeTrim(agency.agencyId),
                        'agencyName': PioneerUtils.safeTrim(agency.agencyName)
                      }
                    );
                    agencies = agencies.push(item);
                  });
                  this.userLocalStorageService.setItem(AuthenticationStore.MULTI_AGENCY_STR, JSON.stringify(agencies));
                  this.defaultAgency.next(JSON.stringify(agencies));
                }
              },
              (error: ExternalSourceError) => {
              }
            );
          }

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  refreshPermissionsFromToken(token: string, externalLogin: boolean): Observable<UserToken> {

    // Make sure token is not expired
    if (this.isTokenExpired()) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.refreshPermissions(token)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token, null);

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  assignDefaultArcIata(token: string, externalLogin: boolean, agencyId: string): Observable<UserToken> {

    // Make sure token is not expired
    if (this.isTokenExpired()) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }


    return this.authenticationHttpService.getPermissions(token, agencyId)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token, null);

          if (userPermission && userPermission.agencyInfo
                              && userPermission.agencyInfo.defaultArcIata
                              && userPermission.agencyInfo.defaultArcIata != null) {
            this.userLocalStorageService.setItem(userPermission.jti.toUpperCase() + AuthenticationStore.DEFAULT_AGENCY_STR,
                                                 JSON.stringify(userPermission.agencyInfo.defaultArcIata));
            this.userLocalStorageService.setItem(userPermission.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR,
                                                 JSON.stringify(userPermission.agencyInfo.defaultArcIata));
            this.defaultAgency.next(userPermission.agencyInfo.defaultArcIata);
          }

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  getToken(): string {
    return this.userLocalStorageService.getToken();
  }

  getUserPermissionToken(): UserToken {
    return this.userLocalStorageService.getUserPermissionToken();
  }

  removeToken(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.TOKEN_NAME);
  }

  removeExpToken(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.EXP_TOKEN_NAME);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  removeMultiAgency(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.MULTI_AGENCY_STR);
    this.defaultAgency.next(AuthenticationStore.MULTI_AGENCY_STR);
  }

  isExternalLogin(): boolean {
    const loginSourceStr = this.userLocalStorageService.getItem(AuthenticationStore.EXTERNAL_LOGIN);
    return loginSourceStr === 'true';
  }

  setExternalLogin(externalLogin: boolean): void {
    this.userLocalStorageService.setItem(AuthenticationStore.EXTERNAL_LOGIN, externalLogin ? 'true' : 'false');
  }

  removeExternalLogin(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.EXTERNAL_LOGIN);
  }

  hasPermission(testPermission: string): boolean;
  hasPermission(testPermissions: string[]): boolean;
  hasPermission(arg: string | string[]): boolean {
    let testPermissions: string[];
    if (arg instanceof Array) {
      // arg must be string[]
      testPermissions = arg.map(perm => perm.toLowerCase());
    } else {
      // arg must be a string
      testPermissions = [ arg.toLowerCase() ];
    }

    const userPermissions: string[] = this.userLocalStorageService.getPermissions().map(perm => perm.toLowerCase());
    // this.logService.info(`userPermissions: [${userPermissions}]`);
    if (userPermissions.find((up: string) => isDefined(testPermissions.find(tp => tp === up)))) {
      // this.logService.info(`Permission: true`);
      return true;
    } else {
      // this.logService.info(`Permission: false`);
      return false;
    }
  }

  hasPermissionCommission(testPermission: string): boolean;
  hasPermissionCommission(testPermissions: string[]): boolean;
  hasPermissionCommission(arg: string | string[]): boolean {
    let testPermissions: string[];
    if (arg instanceof Array) {
      // arg must be string[]
      testPermissions = arg.map(perm => perm.toLowerCase());
    } else {
      // arg must be a string
      testPermissions = [ arg.toLowerCase() ];
    }

    const userPermissions: string[] = this.userLocalStorageService.getPermissionsCommission().map(perm => perm.toLowerCase());
    // this.logService.info(`userPermissions: [${userPermissions}]`);
    if (userPermissions.find((up: string) => isDefined(testPermissions.find(tp => tp === up)))) {
      // this.logService.info(`Permission: true`);
      return true;
    } else {
      // this.logService.info(`Permission: false`);
      return false;
    }
  }

  public getTokenExpirationDate(exp: number): Date | null {
    if (exp) {
      return null;
    }
    const date = new Date(0);
    date.setUTCSeconds(exp);
    return date;
  }

  public isTokenExpired(): boolean {
    const expiration = this.userLocalStorageService.getExpToken();
    if (!expiration) {
      return true;
    }

    const date = this.getTokenExpirationDate(expiration);

    if (date === null) {
      return false;
    }
    return !(date.valueOf() > (new Date().valueOf()));
  }

  private isTokenValid(): boolean {
    const isTokenValid = !this.isTokenExpired();
    this.logService.verbose(`isTokenValid: ${isTokenValid}`);
    return isTokenValid;
  }


  /**
   * This method will test to determine if the current token is expired.  It will put those
   * results in the Subject so that listeners will be made aware.
   *
   */
  private updateState() {
    this.userAuthenticated.next(this.isTokenValid());
  }

  isLoggedIn(): Observable<boolean> {
    this.updateState();
    return this.userAuthenticated.asObservable();
  }

  get permissions(): string[] {
    return this.userLocalStorageService.getPermissions();
  }

  logout(routeToLogin = true) {
    this.userLocalStorageService.resetPagesizes();
    this.removeToken();
    this.removeExpToken();
    this.updateState();
    if (routeToLogin) {
      this.router.navigate([this.environment.logout], { queryParams: { 'redirect': true }} );
    }
  }

  private setUserFromToken(token: string, exp: string): void {
    if (!token) {
      return;
    }
    if (!this.isClientContactSetUp) {
      this.setClientContactEditable(token);
    }
    const userPermissionToken = this.getUserPermissionToken();
    if (!userPermissionToken) {
      return;
    }
    let expir = null;
    if (exp) {
      expir = Number(exp) ;
    } else {
      expir = userPermissionToken.exp;
    }
    const user = new User({
      username: userPermissionToken.jti  || '',
      email: userPermissionToken.email || '',
      firstName: userPermissionToken.firstname || '',
      lastName: userPermissionToken.lastname || '',
      expiration: new Date(expir * 1000),
      allChains: userPermissionToken.hotelInfo ? userPermissionToken.hotelInfo.allChains : null,
      allBrands: userPermissionToken.hotelInfo ? userPermissionToken.hotelInfo.allBrands : null,
      allProperties: userPermissionToken.hotelInfo ? userPermissionToken.hotelInfo.allProperties : null,
      allArcIatas: userPermissionToken.agencyInfo ? userPermissionToken.agencyInfo.allArcIatas : null,
      customerInfo: userPermissionToken.agencyInfo ? userPermissionToken.agencyInfo.customerInfo : null,
      type: userPermissionToken.userType
    });
    this.defaultAgency.next(token);
    this.setUserBehaviorSubject(user);
  }

  // marked protected so that MockAuthenticationStore can override as public
  protected setUserBehaviorSubject(user: User) {
    this.userBehaviorSubject.next(user);
  }

  public getUserSync(): User {
    return this.userBehaviorSubject.getValue();
  }

  public refreshToken(token: string, exp: string) {
    this.userLocalStorageService.setToken(token);
    this.userLocalStorageService.setExpToken(exp);
    this.setUserFromToken(token, exp);
    this.updateState();
  }

  public refreshExpToken(exp: string) {
    this.userLocalStorageService.setExpToken(exp);
    this.updateState();
  }

  public isSinglePropertyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.allChains && user.allChains.length === 1
      && user.allBrands && user.allBrands.length === 1
      && user.allProperties && user.allProperties.length === 1);
  }

  public isPropertyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.HOTEL);
  }

  public isMultiPropertyUser(): boolean {
    return this.isPropertyUser() && !this.isSinglePropertyUser();
  }

  public isAgencyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.AGENCY);
  }

  public userType(): string {
    const user =  this.userBehaviorSubject.getValue();
    return UserType[user.type];
  }

  public isSupportUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.SUPPORT);
  }

  public matchUserType(userType: string): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (userType === user.type.toString());
  }

  updateIataFromToken(newArcIata: string): Observable<UserToken> {
    const token = this.getToken();
    const externalLogin = this.isExternalLogin();



    // Make sure token is not expired
    if (this.isTokenExpired()) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.updateIataFromToken(token, newArcIata)
      .catch(error => {
        this.logService.error(`Failed to get update the iata from token`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.userLocalStorageService.setExpToken(userPermission.exp);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token, null);

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  public openDefaultArcIataModalPanel(token: string): boolean {
    const userPermissiontoken = this.getUserPermissionToken();
    if (userPermissiontoken && userPermissiontoken.agencyInfo
      && (!userPermissiontoken.agencyInfo.defaultArcIata || userPermissiontoken.agencyInfo.defaultArcIata == null)) {
      this.userLocalStorageService.removeItem(userPermissiontoken.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR);
      return true;
    }
  }

  public getDefaultArcIata(): string {
    const token = this.getUserPermissionToken();
    if (token) {
      if (token && token.agencyInfo && token.agencyInfo.defaultArcIata) {
        return token.agencyInfo.defaultArcIata.agencyId;
      }
    }
    return undefined;
  }

  private setClientContactEditable(token: string) {

    if (token && !this.isClientContactSetUp) {
      const userPermissiontoken = this.getUserPermissionToken();
      if (userPermissiontoken && userPermissiontoken.agencyInfo
        && userPermissiontoken.agencyInfo.allCustomers
        && userPermissiontoken.agencyInfo.allCustomers != null) {
        this.authenticationHttpService.isClientContactEditable(token).subscribe(
          (data: CustomerConfig) => {
            if (data) {
              this.isClientContactSetUp = true;
              this.clientContactEditable.next(data.clientContactEditable);
            }
          },
          (error: ExternalSourceError) => {
          }
        );
      }
    }
  }

  isMultiVendor(): boolean {
    return this.hasPermission('MultiVendor');
  }

}

export enum ExternalSourceError {
  TOKEN_EXPIRED,
  TOKEN_INVALID_FORMAT,
  DEFAULT_ERROR
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/permission/services/authentication.store.ts